<template>
  <div v-if="ready" class="mobile-locale-dock" :class="{ 'is-dragging': dragging }" :style="positionStyle">
    <LocaleSwitcher ref="switcher" compact bare :menu-style="menuStyle"
      @pointerdown.native="startDrag" @pointermove.native="moveDrag"
      @pointerup.native="endDrag" @pointercancel.native="endDrag"
      @lostpointercapture.native="endDrag" @click.native.capture="guardDragClick"
      @keydown.native="moveWithKeyboard" />
  </div>
</template>

<script>
import LocaleSwitcher from "./LocaleSwitcher.vue";
import { LOCALE_DOCK_STORAGE_KEY, clampLocaleDock, snapLocaleDock, restoreLocaleDock, localeDockMenu } from "../../services/mobile-locale-dock.js";

export default {
  name: "MobileLocaleDock",
  components: { LocaleSwitcher },
  data() {
    return { ready: false, dragging: false, position: { x: 0, y: 0, edge: "right", ratio: .35 },
      viewport: { width: 375, height: 667 }, blockClickUntil: 0 };
  },
  computed: {
    positionStyle() { return { transform: `translate3d(${this.position.x}px, ${this.position.y}px, 0)` }; },
    menuStyle() { return localeDockMenu(this.position, this.viewport); },
  },
  watch: {
    "$route.fullPath"() { this.closeMenu(); },
  },
  mounted() {
    let saved;
    try { saved = JSON.parse(window.localStorage.getItem(LOCALE_DOCK_STORAGE_KEY)); } catch (_) { /* 无存储权限时使用默认位置。 */ }
    this.viewport = this.readViewport();
    this.position = restoreLocaleDock(saved, this.viewport);
    this.ready = true;
    window.addEventListener("resize", this.updateViewport);
    this.visualViewport = window.visualViewport;
    this.visualViewport?.addEventListener("resize", this.updateViewport);
    this.visualViewport?.addEventListener("scroll", this.updateViewport);
  },
  beforeDestroy() {
    this.releaseDrag();
    window.removeEventListener("resize", this.updateViewport);
    this.visualViewport?.removeEventListener("resize", this.updateViewport);
    this.visualViewport?.removeEventListener("scroll", this.updateViewport);
  },
  methods: {
    readViewport() {
      const view = window.visualViewport;
      // 安全区探针由应用根节点提供，首次显示前即可读取，不受页面滚动容器影响。
      const style = window.getComputedStyle(document.documentElement);
      const inset = name => parseFloat(style.getPropertyValue(`--mobile-locale-safe-${name}`)) || 0;
      return { width: view?.width || window.innerWidth, height: view?.height || window.innerHeight,
        left: view?.offsetLeft || 0, top: view?.offsetTop || 0,
        safeTop: inset("top"), safeBottom: inset("bottom"), safeLeft: inset("left"), safeRight: inset("right") };
    },
    closeMenu() { if (this.$refs.switcher) this.$refs.switcher.open = false; },
    updateViewport() {
      this.releaseDrag();
      this.viewport = this.readViewport();
      this.position = restoreLocaleDock(this.position, this.viewport);
    },
    persistPosition() {
      try { window.localStorage.setItem(LOCALE_DOCK_STORAGE_KEY, JSON.stringify({ edge: this.position.edge, ratio: this.position.ratio })); } catch (_) { /* 拖动和切换不依赖存储成功。 */ }
    },
    startDrag(event) {
      if (event.isPrimary === false || (event.pointerType === "mouse" && event.button !== 0)) return;
      const trigger = event.target.closest?.(".locale-trigger");
      if (!trigger) return;
      this.viewport = this.readViewport();
      this.drag = { pointerId: event.pointerId, clientX: event.clientX, clientY: event.clientY,
        x: this.position.x, y: this.position.y, moved: false, trigger };
      trigger.setPointerCapture?.(event.pointerId);
    },
    moveDrag(event) {
      const drag = this.drag;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const dx = event.clientX - drag.clientX, dy = event.clientY - drag.clientY;
      if (!drag.moved && Math.hypot(dx, dy) < 6) return;
      if (!drag.moved) this.closeMenu();
      drag.moved = true;
      this.dragging = true;
      if (event.cancelable) event.preventDefault();
      const point = clampLocaleDock({ x: drag.x + dx, y: drag.y + dy }, this.viewport);
      this.position = { ...this.position, ...point };
    },
    endDrag(event) {
      const drag = this.drag;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const moved = drag.moved;
      this.releaseDrag();
      if (!moved) return;
      this.blockClickUntil = Date.now() + 300;
      this.position = snapLocaleDock(this.position, this.viewport);
      this.persistPosition();
    },
    releaseDrag() {
      const drag = this.drag;
      this.drag = null;
      this.dragging = false;
      if (drag?.trigger.hasPointerCapture?.(drag.pointerId)) drag.trigger.releasePointerCapture(drag.pointerId);
    },
    guardDragClick(event) {
      if (event.detail !== 0 && Date.now() < this.blockClickUntil) {
        event.preventDefault(); event.stopPropagation();
      }
    },
    moveWithKeyboard(event) {
      if (!event.target.closest?.(".locale-trigger") || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
      event.preventDefault();
      this.closeMenu();
      const edge = event.key === "ArrowLeft" ? "left" : event.key === "ArrowRight" ? "right" : this.position.edge;
      const dy = event.key === "ArrowUp" ? -16 : event.key === "ArrowDown" ? 16 : 0;
      const docked = restoreLocaleDock({ ...this.position, edge }, this.viewport);
      this.position = snapLocaleDock({ ...docked, y: docked.y + dy }, this.viewport);
      this.persistPosition();
    },
  },
};
</script>

<style lang="less" scoped>
.mobile-locale-dock {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9000;
  width: 44px;
  height: 44px;
  transition: transform 180ms cubic-bezier(.2, .8, .2, 1);

  &.is-dragging { transition: none; }

  ::v-deep .locale-trigger {
    width: 44px;
    height: 44px;
    color: #475569;
    touch-action: none;
    user-select: none;
    -webkit-tap-highlight-color: transparent;

    svg { width: 28px; height: 28px; }
  }

  @media (prefers-reduced-motion: reduce) { transition: none; }
}
</style>
